import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../shared/apiClient';
import { isPaywallDisabled } from '../shared/config/paywall';
import { useAuth } from './AuthContext';

export interface SubscriptionRow {
  id: string;
  organization_id: string;
  plan_id: string;
  status: string;
  billing_cycle: string;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  plan_slug: string;
  plan_name: string;
  plan_tagline?: string | null;
  price_monthly_cents: number;
  price_annual_cents: number;
  max_products: number;
  max_team_members: number;
  features?: unknown;
}

export interface SubscriptionUsage {
  products: number;
  members: number;
}

interface SubscriptionState {
  subscription: SubscriptionRow | null;
  usage: SubscriptionUsage | null;
  loading: boolean;
  error: string | null;
}

interface SubscriptionContextValue extends SubscriptionState {
  refresh: () => Promise<void>;
  /** Paid or valid trial */
  isActive: boolean;
  daysLeftInTrial: number | null;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

function computeIsActive(sub: SubscriptionRow | null): boolean {
  if (!sub) return false;
  if (sub.status === 'active') return true;
  if (sub.status === 'trialing' && sub.trial_ends_at) {
    return new Date(sub.trial_ends_at).getTime() > Date.now();
  }
  return false;
}

function computeDaysLeft(trialEnds: string | null): number | null {
  if (!trialEnds) return null;
  const end = new Date(trialEnds).getTime();
  const diff = end - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { currentOrg, user, initialized } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscription: null,
    usage: null,
    loading: true,
    error: null,
  });

  const userId = user?.id;
  const orgId = currentOrg?.id;
  const refresh = useCallback(async () => {
    if (!userId || !orgId) {
      setState({ subscription: null, usage: null, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await apiFetch('/api/billing/subscription', {
        headers: { 'X-Organization-Id': orgId },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setState({
          subscription: null,
          usage: null,
          loading: false,
          error: (err as { error?: string }).error || 'Failed to load subscription',
        });
        return;
      }
      const data = await res.json();
      setState({
        subscription: data.subscription || null,
        usage: data.usage || null,
        loading: false,
        error: null,
      });
    } catch (e) {
      setState({
        subscription: null,
        usage: null,
        loading: false,
        error: 'Network error',
      });
    }
  }, [userId, orgId]);

  useEffect(() => {
    if (!initialized) return;
    void refresh();
  }, [initialized, refresh]);

  const isActive = useMemo(() => {
    if (isPaywallDisabled()) return true;
    if (!currentOrg?.id) return true;
    if (process.env.NODE_ENV === 'development' && !state.subscription) return true;
    return computeIsActive(state.subscription);
  }, [currentOrg?.id, state.subscription]);

  const daysLeftInTrial = useMemo(() => {
    if (state.subscription?.status !== 'trialing') return null;
    return computeDaysLeft(state.subscription.trial_ends_at);
  }, [state.subscription]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      isActive,
      daysLeftInTrial,
    }),
    [state, refresh, isActive, daysLeftInTrial]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return ctx;
}
